import type { CreatePetRequest, PetDto, WorldDto } from '@tama/api-contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';

/**
 * Cache keys carry the player's id.
 *
 * The alternative — one fixed key plus a `queryClient.clear()` when the identity
 * changes — is what shipped first, and it deadlocked the app on a hard reload:
 * restoring the session from localStorage is fast enough that the screen's
 * queries mount in the same commit the identity first becomes known, so the
 * clear cancelled two in-flight fetches and left their observers pending with
 * nothing to retrigger them. React's StrictMode double-invokes effects in
 * development only, which re-subscribed the observers and hid it locally right
 * up to production.
 *
 * Putting the identity in the key removes the imperative step entirely: a
 * different player simply reads a different entry, and the old one is collected
 * on its own. There is no race left to lose.
 */
export const petQueryKey = (userId: string) => ['pet', userId] as const;
export const worldQueryKey = (userId: string) => ['world', userId] as const;

/**
 * The player's pet.
 *
 * Nothing polls any more. The pet has no stats to decay and cannot die, so its
 * server state only changes when this client changes it — a refetch on focus is
 * enough to pick up what another device did.
 *
 * A missing pet (404) is a normal state — the player has not hatched one yet —
 * so it resolves to `null` instead of throwing, and is not retried.
 */
export function usePet(userId: string) {
  return useQuery<PetDto | null>({
    queryKey: petQueryKey(userId),
    queryFn: async () => {
      try {
        return await api.getPet();
      } catch (error) {
        if (error instanceof ApiError && error.code === 'PET_NOT_FOUND') return null;
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 3,
  });
}

export function useCreatePet(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<PetDto, Error, CreatePetRequest>({
    mutationFn: (input) => api.createPet(input),
    onSuccess: (pet) => queryClient.setQueryData(petQueryKey(userId), pet),
  });
}

/**
 * Releases the pet.
 *
 * Sets the cache to `null` rather than invalidating it: `null` is the honest
 * "no pet" state the query already models, and it is what `useAutoHatch`
 * watches for. The world is untouched — the home outlives its occupant.
 */
export function useDeletePet(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => api.deletePet(),
    onSuccess: () => queryClient.setQueryData(petQueryKey(userId), null),
  });
}

/**
 * The player's home map, seeded by the server on first read.
 *
 * `staleTime: Infinity` because the world is only ever changed by this client,
 * through the mutations below — a background refetch could only ever overwrite
 * an in-progress edit with what the server already agreed to.
 */
export function useWorld(userId: string) {
  return useQuery<WorldDto>({
    queryKey: worldQueryKey(userId),
    queryFn: () => api.getWorld(),
    staleTime: Number.POSITIVE_INFINITY,
    retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 3,
  });
}

/**
 * Saves an edited world.
 *
 * The response, not the request, goes into the cache: the server normalises what
 * it stores, so what comes back is what the next read would return.
 */
export function useSaveWorld(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<WorldDto, Error, WorldDto>({
    mutationFn: (world) => api.saveWorld(world),
    onSuccess: (world) => queryClient.setQueryData(worldQueryKey(userId), world),
  });
}

/** Throws the player's edits away and restores the map the game ships with. */
export function useResetWorld(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<WorldDto, Error, void>({
    mutationFn: () => api.resetWorld(),
    onSuccess: (world) => queryClient.setQueryData(worldQueryKey(userId), world),
  });
}
